import { Check } from "lucide-react";

const services = [
  {
    n: "01",
    title: "AI Security & Compliance Assessment",
    desc:
      "We test your AI applications for security vulnerabilities and map every finding to your applicable regulatory framework — HIPAA, SOC 2, HITRUST, CMMC, or NIST AI RMF — with dollar exposure and an audit-ready evidence package attached. Deliverables your board, your auditor, and your legal team can act on.",
    items: [
      "Full application and AI security findings report",
      "Regulatory gap map: HIPAA · SOC 2 · HITRUST · CMMC · NIST AI RMF",
      "Vendor AI Risk Register",
      "AI Governance Policy Template",
      "Executive and board-ready presentation",
    ],
    price: "Scoped to your environment. Proposal within 48 hours",
  },
  {
    n: "02",
    title: "AI Red Team",
    desc:
      "Adversarial simulation across your full stack. We attack it the way a malicious actor would: prompt injection, jailbreaks, data exfiltration, model inversion, multi-turn orchestration. Every exploit mapped to a regulatory citation and dollar exposure.",
    items: [
      "Full red team findings report",
      "MITRE ATLAS and OWASP framework mapping",
      "Regulatory citation per finding",
      "Prioritized remediation roadmap",
      "Pre-launch compliance certification package",
    ],
    price: "Scoped to your attack surface. Proposal within 48 hours",
  },
  {
    n: "03",
    title: "Application Security",
    tagline: "Secure what you build before someone else finds what you missed.",
    desc:
      "Most application vulnerabilities are not found by security teams — they are found by attackers, bug bounty hunters, or regulators. ArqeraAI embeds security into your development lifecycle so that risk is identified and remediated before code reaches production.",
    items: [
      "Threat models mapped to STRIDE with risk-rated findings per component",
      "SAST and SCA coverage across your codebase with enforcement gates in CI/CD pipelines",
      "API security assessment with authorization and authentication gap analysis",
      "Penetration testing governance framework with vendor management and SLA enforcement",
      "Vulnerability management program with SLA tiers, Jira integration, and executive reporting",
      "Secrets management architecture review and implementation guidance",
    ],
    frameworks: "OWASP · STRIDE · NIST · CWE",
    closing: "Every finding comes with a fix, not just a flag.",
    price: "Scoped to your application footprint. Proposal within 48 hours",
  },
  {
    n: "04",
    title: "Privacy & Consent Enforcement",
    tagline: "Your consent banner is not compliance. Proving it works is.",
    desc:
      "Having a cookie banner and enforcing consent are two different things. Most organizations have configured their consent management platform but have never independently verified that non-essential tracking technologies are actually suppressed after rejection. We find the gap, close it, and give you the evidence file your insurance carrier or regulator wants to see.",
    items: [
      "Pre and post-consent tracking audit across GB and US-CA regions with pass/fail per vendor",
      "Google Consent Mode v2 configuration and enforcement validation",
      "Cookie deletion on opt-out deployment and production confirmation",
      "OneTrust, GTM, and tag management remediation with documented change log",
      "CCPA/CPRA and GDPR enforcement verification with regional testing",
      "Insurance-ready evidence package mapped to Privaini or equivalent scan findings",
      "GPC (Global Privacy Control) configuration and signal validation",
    ],
    frameworks: "CCPA · CPRA · GDPR · NIST Privacy Framework",
    closing:
      "We do not just configure your consent tool. We prove it works — and we give you the evidence file.",
    price: "Scoped to your digital surface. Proposal within 48 hours",
  },
  {
    n: "05",
    title: "Remediation and Compliance Monitoring",
    desc:
      "We fix what we find. Fix validation, runtime protection, controlled rollout monitoring, and continuous compliance governance. The ongoing program your board wants to see evidence of.",
    items: [
      "Fix validation and regression testing",
      "Runtime protection deployment",
      "Monthly security posture report",
      "Quarterly compliance re-assessment",
      "Regulatory change monitoring and advisory",
    ],
    price: "Monthly retainer. Scoped to your environment",
  },
  {
    n: "06",
    title: "Safe AI Adoption Program",
    desc:
      "Once your AI foundation is secure, we guide your organization through intelligent, controlled AI adoption: workflow by workflow, system by system. Security is where we start. Scale is where we take you.",
    items: [
      "AI adoption roadmap",
      "Workflow implementation plan",
      "Team enablement and training",
      "AI governance framework",
      "Ongoing strategic advisory",
    ],
    price: "Scoped to your organization. Let's talk.",
  },
];

export default function ArqServices() {
  return (
    <section id="services" className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center max-w-[820px] mx-auto mb-16">
          <div className="arq-label mb-6">What We Do</div>
          <h2 className="font-display font-semibold text-arq-text mb-5" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            Full-Lifecycle Security and Privacy Engineering. From First Assessment to Safe Adoption.
          </h2>
          <p className="text-arq-muted text-[17px] leading-[1.75]">
            We do not just find vulnerabilities. We fix them, prove the fix, and give you the
            evidence file your auditor, regulator, or insurance carrier is asking for.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((s) => (
            <div key={s.n} className="arq-card p-8 flex flex-col">
              <div className="font-mono-tech text-[12px] text-arq-blue mb-3 tracking-[0.08em]">{s.n}</div>
              <h3 className="font-display text-[20px] font-semibold text-arq-text mb-2">{s.title}</h3>
              {s.tagline && (
                <p className="text-arq-teal text-[14px] font-medium italic mb-3">{s.tagline}</p>
              )}
              <p className="text-arq-muted text-[15px] leading-[1.7] mb-6">{s.desc}</p>
              <ul className="space-y-3 mb-6 flex-1">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[15px] text-arq-text/90">
                    <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "hsl(var(--arq-teal))" }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              {s.frameworks && (
                <div className="font-mono-tech text-[11px] text-arq-tertiary tracking-[0.06em] mb-3">
                  FRAMEWORKS: {s.frameworks}
                </div>
              )}
              {s.closing && (
                <p className="text-arq-text text-[14px] italic mb-4">{s.closing}</p>
              )}
              <div className="font-mono-tech text-[12px] text-arq-tertiary pt-5 border-t border-arq-border">
                {s.price}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
